import { api } from '@/shared/api/http'
import { readPreferences, type SessionSnapshot } from '@/features/preferences/types'
import type { LanguageId } from '@/features/language/types'
import type { ProviderId } from '@/shared/ui/providers'

/**
 * provider 동의 화면으로 가는 자리.
 *
 * Backend가 confidential client라 인가 요청 조립과 토큰 교환이 모두 저쪽에 있다.
 * Frontend가 하는 일은 브라우저를 이 주소로 보내는 것뿐이다. Backend가 provider로 302를
 * 내리므로 fetch로 부르지 않고 문서를 통째로 옮긴다.
 *
 * 지금 화면이 서 있는 언어를 함께 싣는다. 계정이 이때 서고 서버가 그 자리에서 닉네임을 지어
 * 넣는데, 아직 Session이 없어 서버가 이 사람의 언어를 알 길이 이 값 하나뿐이다. 싣지 않으면
 * 새 계정이 모두 영어 닉네임을 받는다.
 *
 * 개인을 가리키는 값이 아니라 화면을 그리는 값이라 주소에 실어도 된다.
 */
export function socialLoginAuthorizeUri(provider: ProviderId, languageId: LanguageId): string {
  return `/api/v1/auth/${provider}/authorize?languageId=${languageId}`
}

export function startSocialLogin(provider: ProviderId, languageId: LanguageId): void {
  window.location.assign(socialLoginAuthorizeUri(provider, languageId))
}

/**
 * 지금 브라우저가 살아 있는 Session을 들고 있는지 서버에 묻고 계정에 남은 화면 설정을 함께 받는다.
 *
 * Session 쿠키가 HttpOnly라 JS가 값을 읽지 못한다. 로그인 상태를 아는 길은 물어보는 것뿐이고
 * 살아 있는지 아닌지는 여전히 상태 코드가 가른다. 본문은 그 판정에 끼어들지 않고 화면을 그리는
 * 데 쓰이는 값만 나른다.
 *
 * 앱을 열 때와 창이 앞으로 나올 때, 화면을 옮길 때 이 요청이 돈다. 선호값이 여기 실려 있어
 * 다른 기기에서 바꾼 값이 그 길로 따라온다.
 */
export async function fetchSession(signal?: AbortSignal): Promise<SessionSnapshot> {
  const body = await api.get<unknown>('/me', signal ? { signal } : {})
  return { preferences: readPreferences(body) }
}

export function logout(): Promise<void> {
  return api.post<void>('/auth/logout')
}

/**
 * 로그아웃을 마친 사람을 로그인 화면으로 보내는 자리.
 *
 * 화면 안에서 경로만 바꾸지 않고 문서를 통째로 옮긴다. 경로만 바꾸면 방금 끊은 Session의
 * 판정이 메모리에 남아 있어 로그인 화면이 아직 인증된 것으로 읽고 홈으로 되돌린다. 사용자
 * 눈에는 버튼이 먹지 않은 것으로 보인다.
 *
 * 통째로 옮기면 앞선 사용자의 값이 메모리에 하나도 남지 않는다. 공용 기기에서 나가는 일이라
 * 그 편이 맞다.
 */
export function goToLogin(): void {
  window.location.assign('/login')
}
